import { useEffect, useRef, useState } from "react";
import { formatDate, formatDateTime, DATE_PLACEHOLDER, DATETIME_PLACEHOLDER } from "@/lib/dateFmt";
import { CalendarBlank, CaretLeft, CaretRight } from "@phosphor-icons/react";

/**
 * A friendly date picker that supports zoom-out navigation:
 *   day view  → month view  → year view  → decade view.
 * Click the header to zoom out, click a cell to zoom back in.
 *
 * Props:
 *   value:  string in yyyy-MM-dd or full ISO datetime (or empty)
 *   onChange(next): called with a yyyy-MM-dd or yyyy-MM-ddTHH:mm string
 *   withTime: bool — if true, also renders HH:mm sliders
 *   testId: root data-testid
 */
export default function DatePickerZoom({ value, onChange, withTime = false, testId = "date-picker", placeholder }) {
  const parsed = value ? new Date(value) : null;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("day"); // day / month / year / decade
  const [anchor, setAnchor] = useState(parsed && !isNaN(parsed) ? parsed : new Date());
  const [hh, setHH] = useState(parsed && !isNaN(parsed) ? parsed.getHours() : 9);
  const [mm, setMM] = useState(parsed && !isNaN(parsed) ? parsed.getMinutes() : 0);
  // Free-text buffer: enables typing dd-MM-yyyy directly (Issue #22)
  const [typing, setTyping] = useState(false);
  const [typedText, setTypedText] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const display = value ? (withTime ? formatDateTime(value) : formatDate(value)) : "";

  function commitDate(y, m, d) {
    const iso = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    if (withTime) {
      onChange(`${iso}T${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
    } else {
      onChange(iso);
    }
    setOpen(false);
    setView("day");
  }

  function handleTypedChange(e) {
    // Allow only digits + separators; auto-insert dashes and colons.
    const raw = e.target.value.replace(/[^\d]/g, "");
    let formatted = "";
    if (!withTime) {
      const digits = raw.slice(0, 8);
      if (digits.length >= 5) {
        formatted = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
      } else if (digits.length >= 3) {
        formatted = `${digits.slice(0, 2)}-${digits.slice(2)}`;
      } else {
        formatted = digits;
      }
    } else {
      const digits = raw.slice(0, 12);
      const d = digits.slice(0, 2);
      const mo = digits.slice(2, 4);
      const y = digits.slice(4, 8);
      const H = digits.slice(8, 10);
      const M = digits.slice(10, 12);
      formatted = d;
      if (mo) formatted += "-" + mo;
      if (y) formatted += "-" + y;
      if (H) formatted += " " + H;
      if (M) formatted += ":" + M;
    }
    setTypedText(formatted);

    const dateMatch = /^(\d{2})-(\d{2})-(\d{4})(?:\s(\d{2}):(\d{2}))?$/.exec(formatted);
    if (dateMatch) {
      const day = parseInt(dateMatch[1], 10);
      const month = parseInt(dateMatch[2], 10);
      const year = parseInt(dateMatch[3], 10);
      if (month >= 1 && month <= 12) {
        const daysInMonth = new Date(year, month, 0).getDate();
        if (day >= 1 && day <= daysInMonth) {
          const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          // Keep the calendar anchor in sync with what was just typed so opening
          // the popup doesn't scroll back to today's month.
          setAnchor(new Date(year, month - 1, day));
          if (withTime) {
            const H = parseInt(dateMatch[4] || String(hh).padStart(2, "0"), 10);
            const M = parseInt(dateMatch[5] || String(mm).padStart(2, "0"), 10);
            if (H >= 0 && H <= 23 && M >= 0 && M <= 59) {
              setHH(H);
              setMM(M);
              onChange(`${iso}T${String(H).padStart(2, "0")}:${String(M).padStart(2, "0")}`);
            }
          } else {
            onChange(iso);
          }
        }
      }
    } else if (formatted === "") {
      onChange("");
    }
  }

  function stopTyping() {
    setTyping(false);
    setTypedText("");
  }

  function handleKeyDown(e) {
    if (e.key === "Escape") {
      if (open) {
        // Only intercept when our own popup is open so we don't fight the
        // parent Radix Dialog's Escape handler.
        e.stopPropagation();
        e.preventDefault();
        setOpen(false);
        e.currentTarget.blur();
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      <div className="relative">
        <input
          value={typing ? typedText : display}
          placeholder={placeholder || (withTime ? DATETIME_PLACEHOLDER : DATE_PLACEHOLDER)}
          onFocus={() => {
            setTyping(true);
            setTypedText(display);
          }}
          onBlur={() => {
            // Small delay so click-inside pop-up doesn't cancel the picker
            setTimeout(stopTyping, 150);
          }}
          onChange={handleTypedChange}
          onClick={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          data-testid={`${testId}-input`}
          inputMode="numeric"
          maxLength={withTime ? 16 : 10}
          className="pf-input pr-9 cursor-text"
        />
        <CalendarBlank
          size={14}
          onClick={() => setOpen((o) => !o)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-pf-text-secondary cursor-pointer"
        />
        {display && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
              setTypedText("");
            }}
            className="absolute right-8 top-1/2 -translate-y-1/2 text-pf-text-secondary hover:text-pf-danger text-xs"
            title="Clear"
          >
            ✕
          </button>
        )}
      </div>

      {open && (
        <>
          {/* Mobile-only backdrop that turns the picker into a full-screen sheet */}
          <div
            className="fixed inset-0 z-40 bg-black/40 sm:hidden"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-x-2 top-16 z-50 pf-card p-3 bg-white shadow-dropdown max-h-[85vh] overflow-y-auto sm:absolute sm:inset-x-auto sm:top-auto sm:z-40 sm:mt-1 sm:left-0 sm:w-[min(300px,calc(100vw-2rem))] sm:max-w-[calc(100vw-1rem)]">
            <div className="sm:hidden mb-3">
              <input
                value={typedText || display}
                onChange={handleTypedChange}
                placeholder={placeholder || (withTime ? DATETIME_PLACEHOLDER : DATE_PLACEHOLDER)}
                inputMode="numeric"
                maxLength={withTime ? 16 : 10}
                className="pf-input"
                data-testid={`${testId}-mobile-input`}
              />
            </div>
            <Header view={view} anchor={anchor} setAnchor={setAnchor} setView={setView} />
            <div className="mt-2">
              {view === "day" && <DayGrid anchor={anchor} onPick={(d) => commitDate(anchor.getFullYear(), anchor.getMonth(), d)} value={parsed} />}
              {view === "month" && (
                <MonthGrid
                  onPick={(m) => {
                    setAnchor(new Date(anchor.getFullYear(), m, 1));
                    setView("day");
                  }}
                  currentMonth={anchor.getMonth()}
                />
              )}
              {view === "year" && (
                <YearGrid
                  anchor={anchor}
                  onPick={(y) => {
                    setAnchor(new Date(y, anchor.getMonth(), 1));
                    setView("month");
                  }}
                  currentYear={anchor.getFullYear()}
                />
              )}
              {view === "decade" && (
                <DecadeGrid
                  anchor={anchor}
                  onPick={(y) => {
                    setAnchor(new Date(y, anchor.getMonth(), 1));
                    setView("year");
                  }}
                />
              )}
            </div>
            {withTime && view === "day" && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-pf-border">
                <span className="text-xs pf-label">Ora</span>
                <input type="number" min="0" max="23" className="pf-input !py-1 w-16" value={hh} onChange={(e) => setHH(Math.max(0, Math.min(23, parseInt(e.target.value || "0"))))} />
                <span>:</span>
                <input type="number" min="0" max="59" className="pf-input !py-1 w-16" value={mm} onChange={(e) => setMM(Math.max(0, Math.min(59, parseInt(e.target.value || "0"))))} />
                <button
                  type="button"
                  className="pf-btn-primary ml-auto !py-1 !px-2 text-xs"
                  onClick={() => parsed && commitDate(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())}
                  disabled={!parsed}
                >
                  Set time
                </button>
              </div>
            )}
            <div className="flex justify-between items-center mt-3 pt-3 border-t border-pf-border text-xs gap-2">
              <button type="button" onClick={() => { const t=new Date(); setAnchor(t); commitDate(t.getFullYear(), t.getMonth(), t.getDate()); }} className="text-pf-primary hover:underline" data-testid={`${testId}-today`}>Today</button>
              <div className="flex gap-2">
                <button type="button" onClick={() => setOpen(false)} className="pf-btn-ghost !py-1 !px-3" data-testid={`${testId}-close`}>Close</button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="pf-btn-primary !py-1 !px-3"
                  data-testid={`${testId}-accept`}
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Header({ view, anchor, setAnchor, setView }) {
  const monthName = anchor.toLocaleDateString(undefined, { month: "long" });
  const year = anchor.getFullYear();
  const decadeStart = Math.floor(year / 10) * 10;

  let title = "";
  let nextView = view;
  if (view === "day") { title = `${monthName} ${year}`; nextView = "month"; }
  else if (view === "month") { title = `${year}`; nextView = "year"; }
  else if (view === "year") { title = `${decadeStart}–${decadeStart + 9}`; nextView = "decade"; }
  else if (view === "decade") { title = `${Math.floor(year / 100) * 100}s`; }

  function shift(step) {
    const d = new Date(anchor);
    if (view === "day") d.setMonth(d.getMonth() + step);
    else if (view === "month") d.setFullYear(d.getFullYear() + step);
    else if (view === "year") d.setFullYear(d.getFullYear() + step * 10);
    else if (view === "decade") d.setFullYear(d.getFullYear() + step * 100);
    setAnchor(d);
  }

  return (
    <div className="flex items-center justify-between">
      <button type="button" onClick={() => shift(-1)} className="p-1.5 rounded hover:bg-pf-bg text-pf-text-secondary">
        <CaretLeft size={14} />
      </button>
      <button
        type="button"
        onClick={() => setView(nextView)}
        className="text-sm font-semibold text-pf-text hover:text-pf-primary px-2 py-1 rounded"
      >
        {title}
      </button>
      <button type="button" onClick={() => shift(1)} className="p-1.5 rounded hover:bg-pf-bg text-pf-text-secondary">
        <CaretRight size={14} />
      </button>
    </div>
  );
}

function DayGrid({ anchor, onPick, value }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const startDay = (first.getDay() + 6) % 7; // Monday first
  const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
  const today = new Date();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <div className="grid grid-cols-7 text-[10px] uppercase text-pf-text-secondary mb-1 font-semibold">
        {["L", "M", "M", "J", "V", "S", "D"].map((w, i) => <div key={i} className="text-center py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const isToday = today.getDate() === d && today.getMonth() === anchor.getMonth() && today.getFullYear() === anchor.getFullYear();
          const isSelected = value && value.getDate() === d && value.getMonth() === anchor.getMonth() && value.getFullYear() === anchor.getFullYear();
          return (
            <button
              key={i}
              type="button"
              onClick={() => onPick(d)}
              className={`aspect-square text-sm rounded transition-colors ${
                isSelected ? "bg-pf-primary text-white font-semibold" : isToday ? "border border-pf-primary text-pf-primary" : "hover:bg-pf-bg"
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MonthGrid({ onPick, currentMonth }) {
  const months = Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleDateString(undefined, { month: "short" }));
  return (
    <div className="grid grid-cols-3 gap-2 py-2">
      {months.map((m, i) => (
        <button key={i} type="button" onClick={() => onPick(i)}
          className={`py-3 rounded text-sm transition-colors ${i === currentMonth ? "bg-pf-primary text-white font-semibold" : "hover:bg-pf-bg"}`}>
          {m}
        </button>
      ))}
    </div>
  );
}

function YearGrid({ anchor, onPick, currentYear }) {
  const start = Math.floor(anchor.getFullYear() / 10) * 10;
  const years = Array.from({ length: 10 }, (_, i) => start + i);
  return (
    <div className="grid grid-cols-3 gap-2 py-2">
      {years.map((y) => (
        <button key={y} type="button" onClick={() => onPick(y)}
          className={`py-3 rounded text-sm transition-colors ${y === currentYear ? "bg-pf-primary text-white font-semibold" : "hover:bg-pf-bg"}`}>
          {y}
        </button>
      ))}
    </div>
  );
}

function DecadeGrid({ anchor, onPick }) {
  const start = Math.floor(anchor.getFullYear() / 100) * 100;
  const decades = Array.from({ length: 10 }, (_, i) => start + i * 10);
  return (
    <div className="grid grid-cols-3 gap-2 py-2">
      {decades.map((y) => (
        <button key={y} type="button" onClick={() => onPick(y)}
          className="py-3 rounded text-sm hover:bg-pf-bg">
          {y}s
        </button>
      ))}
    </div>
  );
}
